import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const STORAGE_KEY = 'wrestling_coach_ready_countdown_target';
const ONE_DAY = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + ONE_DAY;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + ONE_DAY;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* localStorage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(ONE_DAY);
  useEffect(() => setTarget(getInitialTarget()), []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + ONE_DAY;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* localStorage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(ONE_DAY);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-navy border border-gold/50 rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_rgba(15,42,82,0.25)]" data-id="element-59" data-mp="1pz55gylokolj">
      <div className="text-center" data-id="element-60" data-mp="2san0ivsn77m0">
        <div className="flex items-center justify-center gap-2 text-gold-light font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-61" data-mp="2nd4tjay6r2gn">
          <AlarmClock size={20} data-id="element-62" data-mp="2jahxsyye8xe6" />
          <span data-id="element-63" data-mp="2ecygfq6eg4i0">⏳ The Launch Price Ends Soon</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-64" data-mp="2ecggaigm964t">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-65" data-mp="29en85jvr621w" />
          <span className="text-3xl md:text-5xl font-bold text-gold-light" data-id="element-66" data-mp="24n56etlwnxfv">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-67" data-mp="24l27tl6osvzj" />
          <span className="text-3xl md:text-5xl font-bold text-gold-light" data-id="element-68" data-mp="1zp3pae81i871">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-69" data-mp="1vn08rb6ikdew" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-white mb-8" data-id="element-70" data-mp="1vknkyhe2r8tx">
          After this, the price goes up.
        </p>
        <div className="space-y-2" data-id="element-71" data-mp="1lpc2bnzjphgw">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-72" data-mp="1lqdrewdec4hm">
            <span className="text-white" data-id="element-73" data-mp="18crm8ifxncn">🕐 Launch pricing spots remaining</span>
            <span className="text-gold-light" data-id="element-74" data-mp="63i8gswzkbtv">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-navy-dark rounded-full overflow-hidden border border-gold/30" data-id="element-75" data-mp="b5r2lsllhjxk">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '65%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className="h-full bg-gold rounded-full" data-id="element-76" data-mp="f5hr1pnnfnyx" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-white/80 font-medium" data-id="element-77" data-mp="3nl1gedl78m8p">
            65% of spots taken
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-white border-2 border-gold rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-78" data-mp="2zw0e782ym7bv">
      <div className="flex items-baseline justify-center gap-1" data-id="element-79" data-mp="2gqu8rsqtbw2s">
        <span className="text-4xl md:text-5xl font-bold text-navy tabular-nums" data-id="element-80" data-mp="2bskbnclj2lej">{value}</span>
        <span className="text-xs md:text-sm font-medium text-gold-dark" data-id="element-81" data-mp="2bqppjuyisn8v">{label}</span>
      </div>
    </div>;
}